/**
 * Matrix Column Drawing Utility
 *
 * Renders a column of falling characters on a canvas with the classic
 * Matrix "digital rain" look: bright head, fading green trail.
 */

import type { MatrixColumn } from './types';

/**
 * Draw a single column's characters on the canvas.
 *
 * @param ctx - Canvas 2D rendering context
 * @param column - Column data
 * @param cellWidth - Width of a character cell in pixels
 * @param cellHeight - Height of a character cell in pixels
 * @param fontSize - Font size in pixels
 * @param numRows - Total number of rows in the canvas grid
 */
export function drawMatrixColumn(
  ctx: CanvasRenderingContext2D,
  column: MatrixColumn,
  cellWidth: number,
  cellHeight: number,
  fontSize: number,
  numRows: number,
): void {
  const x = column.columnIndex * cellWidth + cellWidth / 2;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';

  for (let i = 0; i < column.trailLength; i++) {
    const row = Math.floor(column.headRow) - i;
    if (row < 0 || row >= numRows) continue;

    const y = row * cellHeight + cellHeight;
    const char = column.chars[i % column.chars.length];

    if (i === 0) {
      // Head character: bright white-green with glow
      ctx.shadowColor = '#00ff00';
      ctx.shadowBlur = 8;
      ctx.fillStyle = '#ccffcc';
      ctx.font = `bold ${fontSize}px monospace`;
      ctx.fillText(char, x, y);
      // Reset shadow immediately for performance
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
    } else {
      // Trail character: fade from bright green to dark green
      const fadeRatio = i / column.trailLength;
      const green = Math.floor(255 - fadeRatio * 180);
      const alpha = 1.0 - fadeRatio * 0.7;
      ctx.fillStyle = `rgba(0, ${green}, 0, ${alpha})`;
      ctx.font = `${fontSize}px monospace`;
      ctx.fillText(char, x, y);
    }
  }
}
